/**
 * Copyright (c) 2025, CYBRA Corp. All rights reserved.
 */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CartonAuditService, CartonAuditService_StateInfoDto, CartonAuditPortalAttemptLineDto,CartonAuditService_StateEnum } from 'app/services/carton-audit.service';
import { SimpleSuccessErrorDialogComponent, ERROR_DIALOG_TITLE } from 'app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { ServiceInstance } from 'app/models/service-instance';
import { ServiceService } from 'app/services/service.service';
@Component({
  selector: 'app-carton-audit-dashboard',
  templateUrl: './carton-audit-dashboard.component.html',
  styleUrls: ['./carton-audit-dashboard.component.css']
})
export class CartonAuditDashboardComponent implements OnInit, OnDestroy {
  serviceId: string | null = null;
  serviceInstance: ServiceInstance | null = null;
  auditState: CartonAuditService_StateInfoDto | null = null;
  isActionBeingPerformed = false;
  private refreshLoopTimer: any;
  private readonly RefreshDelayInMs = 500;

  /** Flag used to indicate if the component has been destroyed or not.  This is used by the background "Refresh Data" task to indicate when it should stop. */
  isDestroyed = false;

  @ViewChild('successErrorDialog') simpleSuccessErrorDialogComponent!: SimpleSuccessErrorDialogComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cartonAuditService: CartonAuditService,
    private serviceService: ServiceService
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe(async (data: { auditState: CartonAuditService_StateInfoDto }) => {
      this.auditState = data?.auditState;
      this.serviceId = this.route.snapshot.paramMap.get('id');

      if (this.serviceId) {
        this.serviceInstance = await this.serviceService.getServiceInstanceById(this.serviceId);
      }

      // Redirect if state requires
      switch (this.auditState?.state) {
        case CartonAuditService_StateEnum.EnterCartonId:
          this.router.navigate(['jockey', 'carton-audit', this.serviceId, 'enter-info']);
          return;
        case CartonAuditService_StateEnum.CartonBeingAudited:
          break;
        default:
          this.simpleSuccessErrorDialogComponent.showErrorDialog('Error', `Unhandled state: ${this.auditState?.state}`);
          this.router.navigate(['jockey', 'carton-audit', 'select-carton']);
          return;
      }

      this.startRefreshLoop();
    });
  }

  /**
   * Destroys the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnDestroy(): void {
    this.stopRefreshLoop();
  }

  private startRefreshLoop(): void {
    // Stop the Refresh Loop if it is currently running
    this.stopRefreshLoop();
    
    // Start a new Refresh Loop
    this.refreshLoopTimer = setInterval(
      async () => 
        {
          if (!this.serviceId) return;
          try {
            this.auditState = await this.cartonAuditService.getCurrentState(this.serviceId);
          } catch (err) {
            console.error('Auto-refresh failed', err);
          }
        }, 
      this.RefreshDelayInMs);
  }

  private stopRefreshLoop(): void {
    if (this.refreshLoopTimer) {
       clearInterval(this.refreshLoopTimer);
    }
  }

  async refreshState() {
    if (!this.serviceId) return;
    try {
      this.auditState = await this.cartonAuditService.getCurrentState(this.serviceId);
    } catch (err) {
      console.error('Failed to refresh state', err);
    }
  }

  // Row Highlight Helpers
  isSuccessRow(item: CartonAuditPortalAttemptLineDto) {
    return item.numberExpected > 0 && item.numberFound === item.numberExpected;
  }
  isPartialRow(item: CartonAuditPortalAttemptLineDto) {
    return item.numberExpected > 0 && item.numberFound > 0 && item.numberFound < item.numberExpected;
  }
  isErrorRow(item: CartonAuditPortalAttemptLineDto) {
    return (item.numberExpected === 0 && item.numberFound > 0) || item.numberFound > item.numberExpected;
  }
  isUnknownRow(item: CartonAuditPortalAttemptLineDto) {
    return item.numberExpected > 0 && (!item.numberFound || item.numberFound === 0);
  }

  // UI Actions
  async onStartReaderClicked() {
    if (!this.serviceId) return;
    this.isActionBeingPerformed = true;
    try {
      await this.cartonAuditService.startReader(this.serviceId);
      await this.refreshState();
    } finally { this.isActionBeingPerformed = false; }
  }

  async onStopReaderClicked() {
    if (!this.serviceId) return;
    this.isActionBeingPerformed = true;
    try {
      await this.cartonAuditService.stopReader(this.serviceId);
      await this.refreshState();
    } finally { this.isActionBeingPerformed = false; }
  }

  async onClearReadsClicked() {
    if (!this.serviceId) return;
    this.isActionBeingPerformed = true;
    try {
      await this.cartonAuditService.clearReads(this.serviceId);
      await this.refreshState();
    } finally { this.isActionBeingPerformed = false; }
  }

  async onClearAllClicked() {
    if (!this.serviceId) return;
    this.isActionBeingPerformed = true;
    try {
      await this.cartonAuditService.clearAll(this.serviceId);
      this.router.navigate(['jockey', 'carton-audit', this.serviceId, 'enter-info']);
    } finally { this.isActionBeingPerformed = false; }
  }

  async onProcessClicked() {
    if (!this.serviceId) return;
    this.isActionBeingPerformed = true;
    try {
      await this.cartonAuditService.process(this.serviceId);
      this.router.navigate(['jockey', 'carton-audit', this.serviceId, 'enter-info']);
    } finally { this.isActionBeingPerformed = false; }
  }

  trackByFunction(index: number, item: CartonAuditPortalAttemptLineDto): any {
    return item.upc;
  }

}
